import { useState, useCallback } from 'react';
import { getPopulation, getSample } from '../lib/supabase';
import { stratifyPopulation, drawSample, applyTossStratum, fmt$, fmtPct } from '../lib/samplingEngine';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const NAVY = '#0D2461', AMBER = '#F5A800', BORDER = '#c8d0e0';

// ── Helpers ────────────────────────────────────────────────────────────────────

function sampleIdsMatch(a, b) {
  if (a.length !== b.length) return false;
  const setA = new Set(a.map(p => String(p._id)));
  return b.every(p => setA.has(String(p._id)));
}

function parseNumber(v) {
  if (v == null) return 0;
  const s = String(v).replace(/[$,\s]/g, '');
  return parseFloat(s) || 0;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function VerifyPanel({ engagements }) {
  const [mode, setMode] = useState('lab'); // 'lab' | 'external'

  return (
    <div>
      <div style={modeRow}>
        <button style={{...modeBtn, ...(mode==='lab'?modeBtnActive:{})}} onClick={() => setMode('lab')}>
          🔁 Replay LAB Engagement
        </button>
        <button style={{...modeBtn, ...(mode==='external'?modeBtnActive:{})}} onClick={() => setMode('external')}>
          📥 External Verification
        </button>
      </div>

      {mode === 'lab'
        ? <LabReplay engagements={engagements} />
        : <ExternalVerify />
      }
    </div>
  );
}

// ── Tab 1: Replay a stored LAB engagement ─────────────────────────────────────

function LabReplay({ engagements }) {
  const [selectedId, setSelectedId] = useState('');
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState(null); // { status, reconstructed, stored, seed, message }
  const [error,      setError]      = useState('');

  const sampledEngs = engagements.filter(e =>
    e.status === 'awaiting_scoring' || e.status === 'complete'
  );

  const handleVerify = async () => {
    if (!selectedId) return;
    setRunning(true); setResult(null); setError('');
    try {
      const [popRes, sampleRes] = await Promise.all([
        getPopulation(selectedId),
        getSample(selectedId),
      ]);
      const population   = popRes.data?.projects;
      const sampleRecord = sampleRes.data;

      if (!population || !sampleRecord) {
        setError('No population or sample data found for this engagement.');
        return;
      }

      const { seed, strata, large100, drawn_projects } = sampleRecord;

      if (!strata || !drawn_projects) {
        setError('Sample record is incomplete — strata or drawn projects missing.');
        return;
      }

      // Re-run only the draw step (Fisher-Yates on the stored strata)
      // This is the exact operation that was originally performed
      const reconstructed = drawSample(strata, large100 || [], seed);
      const match = sampleIdsMatch(reconstructed, drawn_projects);

      setResult({
        status: match ? 'verified' : 'mismatch',
        reconstructed,
        stored: drawn_projects,
        seed,
        totalPop: population.length,
        message: match
          ? `Seed ${seed} reproduces the exact ${reconstructed.length}-item sample on record. Draw is verified.`
          : `Seed ${seed} produces a different sample than what is recorded. This may indicate the population data was modified after the draw, or a settings mismatch.`,
      });
    } catch (err) {
      setError('Verification failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <div style={helpBox}>
        <strong>How this works:</strong> The LAB engine is deterministic — the same seed on the
        same stratified population always produces the same sample. This tool re-runs the stored
        seed against the stored strata to confirm the recorded draw is authentic and unmodified.
      </div>

      <div style={formSection}>
        <label style={label}>Select Engagement</label>
        <select style={select} value={selectedId} onChange={e => { setSelectedId(e.target.value); setResult(null); }}>
          <option value="">— Choose an engagement with a completed draw —</option>
          {sampledEngs.map(e => (
            <option key={e.id} value={e.id}>
              {e.client_name}{e.tax_years ? ` (${e.tax_years})` : ''} — {e.teams?.name || 'Unknown firm'}
            </option>
          ))}
        </select>

        <button style={{...primaryBtn, marginTop:16, opacity:(!selectedId||running)?0.5:1}}
          onClick={handleVerify} disabled={!selectedId || running}>
          {running ? 'Verifying…' : '🔁 Replay Draw & Verify →'}
        </button>
      </div>

      {error && <div style={errorBanner}>{error}</div>}

      {result && (
        <div style={{marginTop:24}}>
          <div style={{
            ...resultBanner,
            background: result.status === 'verified' ? '#1a5c3a' : '#c03a1a',
          }}>
            <div style={resultIcon}>{result.status === 'verified' ? '✓ VERIFIED' : '✗ MISMATCH'}</div>
            <div style={resultMsg}>{result.message}</div>
          </div>

          <div style={metaRow}>
            <MetaCell label="Seed" value={String(result.seed)} mono />
            <MetaCell label="Population Size" value={result.totalPop?.toLocaleString() ?? '—'} />
            <MetaCell label="Reconstructed Sample" value={result.reconstructed.length} />
            <MetaCell label="Stored Sample" value={result.stored.length} />
          </div>

          <div style={sectionLabel}>Reconstructed Sample (re-drawn from seed)</div>
          <SampleTable projects={result.reconstructed} storedIds={new Set(result.stored.map(p => String(p._id)))} showMatch />

          {result.status === 'mismatch' && (
            <>
              <div style={{...sectionLabel, marginTop:20}}>Stored Sample (on record)</div>
              <SampleTable projects={result.stored} storedIds={new Set(result.reconstructed.map(p => String(p._id)))} showMatch />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Verify an external draw ────────────────────────────────────────────

function ExternalVerify() {
  // File/parse state
  const [rows,      setRows]      = useState(null);
  const [columns,   setColumns]   = useState([]);
  const [nameCol,   setNameCol]   = useState('');
  const [amtCol,    setAmtCol]    = useState('');
  const [yearCol,   setYearCol]   = useState('');
  const [fileError, setFileError] = useState('');
  const [step,      setStep]      = useState('upload'); // 'upload' | 'settings' | 'result'

  // Draw settings
  const [seed,         setSeed]         = useState('');
  const [numStrata,    setNumStrata]    = useState(5);
  const [fixedN,       setFixedN]       = useState(5);
  const [top100N,      setTop100N]      = useState(5);
  const [tossEnabled,  setTossEnabled]  = useState(false);
  const [tossPct,      setTossPct]      = useState(2);

  // Result
  const [drawn,    setDrawn]    = useState(null);
  const [strata,   setStrata]   = useState([]);
  const [large100, setLarge100] = useState([]);
  const [running,  setRunning]  = useState(false);
  const [runError, setRunError] = useState('');

  const handleFile = useCallback(async (file) => {
    setFileError('');
    const parse = (data) => {
      if (!data.length) { setFileError('File appears empty.'); return; }
      const cols = Object.keys(data[0]);
      setColumns(cols);
      setRows(data);
      setNameCol(cols.find(c => /name|project|title|desc/i.test(c)) || cols[0]);
      setAmtCol(cols.find(c => /amount|cost|expense|total|value|budget|wage|qre/i.test(c)) || cols[1] || cols[0]);
      setYearCol(cols.find(c => /year|ty|period/i.test(c)) || '');
      setStep('settings');
    };
    try {
      if (/\.csv$/i.test(file.name) || file.type === 'text/csv') {
        await new Promise((resolve, reject) => {
          Papa.parse(file, {
            header: true, skipEmptyLines: true,
            complete: ({ data, errors }) => errors.length ? reject(new Error('Could not parse CSV.')) : resolve(parse(data)),
          });
        });
      } else {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array', cellFormula: false, cellHTML: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) { setFileError('File appears empty or no sheets found.'); return; }
        const xlsRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        if (!xlsRows || xlsRows.length < 2) { setFileError('File appears empty.'); return; }
        const headers = xlsRows[0].map(h => String(h ?? '').trim()).filter(Boolean);
        if (!headers.length) { setFileError('Could not find a header row.'); return; }
        parse(xlsRows.slice(1).map(row => {
          const obj = {};
          headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
          return obj;
        }).filter(row => headers.some(h => row[h] !== '')));
      }
    } catch (err) {
      setFileError(err?.message || 'Could not read file. Use .xlsx or .csv.');
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleRunDraw = () => {
    setRunError(''); setRunning(true); setDrawn(null);
    try {
      const seedNum = parseInt(seed, 10);
      if (!seed || isNaN(seedNum)) { setRunError('Enter a valid integer seed.'); setRunning(false); return; }

      const population = rows.map((row, i) => ({
        _id: i + 1,
        name: String(row[nameCol] ?? '').trim() || `Item ${i + 1}`,
        amount: parseNumber(row[amtCol]),
        year: yearCol ? String(row[yearCol] ?? '').trim() : undefined,
      })).filter(p => p.amount > 0);

      if (population.length === 0) { setRunError('No rows with a positive amount found — check column mapping.'); setRunning(false); return; }

      const { samplingPool } = applyTossStratum(population, { tossEnabled, tossPct });
      const { strata: st, large100: l100 } = stratifyPopulation(samplingPool, {
        sizeMode: 'fixed', fixedN, top100N, numStrata,
      });
      const drawnProjects = drawSample(st, l100, seedNum);

      setStrata(st);
      setLarge100(l100);
      setDrawn(drawnProjects);
      setStep('result');
    } catch (err) {
      setRunError('Draw failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <div style={helpBox}>
        <strong>How this works:</strong> Upload any population file, enter the seed number and
        sampling settings exactly as the original statistician used, and this tool runs the
        LAB algorithm to produce the sample. If the statistician used our engine with the same
        settings, you will get the identical list of projects.
      </div>

      {/* Step 1: Upload */}
      <div style={stepCard}>
        <div style={stepHeader}>Step 1 — Upload Population</div>
        <label
          style={{...dropZone, display:'block', cursor:'pointer'}}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onDragEnter={e => e.preventDefault()}
        >
          {rows
            ? <span style={{color:NAVY,fontWeight:700}}>{rows.length.toLocaleString()} rows loaded ✓  <span style={{fontWeight:400,color:'#8a96b0'}}>click to replace</span></span>
            : <span>Drop a .xlsx or .csv file here, or click to browse</span>}
          <input type="file" accept=".xlsx,.csv,.xls" style={{display:'none'}}
            onChange={e => { if (e.target.files[0]) { handleFile(e.target.files[0]); e.target.value = ''; } }} />
        </label>
        {fileError && <div style={errorBanner}>{fileError}</div>}

        {rows && columns.length > 0 && (
          <div style={colMapGrid}>
            <div style={colMapField}>
              <label style={label}>Project Name Column</label>
              <select style={selectSm} value={nameCol} onChange={e => setNameCol(e.target.value)}>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={colMapField}>
              <label style={label}>Amount / Cost Column</label>
              <select style={selectSm} value={amtCol} onChange={e => setAmtCol(e.target.value)}>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={colMapField}>
              <label style={label}>Tax Year Column <span style={{color:'#8a96b0'}}>(optional)</span></label>
              <select style={selectSm} value={yearCol} onChange={e => setYearCol(e.target.value)}>
                <option value="">— None —</option>
                {columns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Settings */}
      {step !== 'upload' && (
        <div style={stepCard}>
          <div style={stepHeader}>Step 2 — Draw Settings</div>
          <div style={settingsGrid}>
            <div style={settingField}>
              <label style={label}>Random Seed</label>
              <input style={inputSm} type="number" placeholder="e.g. 847291"
                value={seed} onChange={e => setSeed(e.target.value)} />
              <div style={hint}>Integer — must match the original draw exactly</div>
            </div>
            <div style={settingField}>
              <label style={label}>Number of Strata</label>
              <input style={inputSm} type="number" min={2} max={8} value={numStrata}
                onChange={e => setNumStrata(Math.min(8,Math.max(2,parseInt(e.target.value)||5)))} />
            </div>
            <div style={settingField}>
              <label style={label}>Items per Stratum (n)</label>
              <input style={inputSm} type="number" min={1} value={fixedN}
                onChange={e => setFixedN(Math.max(1,parseInt(e.target.value)||5))} />
            </div>
            <div style={settingField}>
              <label style={label}>Certainty (100%) Items</label>
              <input style={inputSm} type="number" min={0} value={top100N}
                onChange={e => setTop100N(Math.max(0,parseInt(e.target.value)||5))} />
              <div style={hint}>Top N projects by dollar amount — always included</div>
            </div>
            <div style={settingField}>
              <label style={label}>Toss Stratum</label>
              <label style={{display:'flex',alignItems:'center',gap:8,marginTop:6,cursor:'pointer'}}>
                <input type="checkbox" checked={tossEnabled} onChange={e => setTossEnabled(e.target.checked)} />
                <span style={{fontFamily:"'Roboto Mono',monospace",fontSize:12,color:NAVY}}>Exclude bottom %</span>
              </label>
            </div>
            {tossEnabled && (
              <div style={settingField}>
                <label style={label}>Toss % (by cumulative amount)</label>
                <input style={inputSm} type="number" min={0.1} max={20} step={0.1} value={tossPct}
                  onChange={e => setTossPct(Math.min(20,Math.max(0.1,parseFloat(e.target.value)||2)))} />
              </div>
            )}
          </div>

          {runError && <div style={errorBanner}>{runError}</div>}

          <button style={{...primaryBtn, marginTop:20, opacity:running?0.5:1}} onClick={handleRunDraw} disabled={running}>
            {running ? 'Running…' : '▶ Run Draw →'}
          </button>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 'result' && drawn && (
        <div style={stepCard}>
          <div style={stepHeader}>Step 3 — Draw Results</div>

          <div style={{...resultBanner, background:NAVY, marginBottom:20}}>
            <div style={resultIcon}>Draw Complete</div>
            <div style={resultMsg}>
              Seed <strong>{seed}</strong> on {rows?.length?.toLocaleString()} projects ({
              (rows?.length ?? 0) - strata.reduce((s,st)=>s+st.projects.length,0) - large100.length
              } tossed if applicable) → <strong>{drawn.length} items</strong> selected
            </div>
          </div>

          <div style={metaRow}>
            <MetaCell label="Seed" value={String(seed)} mono />
            <MetaCell label="Population" value={(rows?.length ?? 0).toLocaleString()} />
            <MetaCell label="Certainty Items" value={large100.length} />
            <MetaCell label="Sampled Items" value={drawn.filter(p=>!p.isLarge).length} />
            <MetaCell label="Total Sample" value={drawn.length} />
          </div>

          <div style={sectionLabel}>Stratum Summary</div>
          <table style={tbl}>
            <thead><tr>
              {['Stratum','Population (Ni)','Sample (n)','Sampling Rate','Total Amount'].map(h=><th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {large100.length > 0 && (
                <tr style={trStyle}>
                  <td style={{...td,color:'#c03a1a',fontWeight:700}}>100% Certainty</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{large100.length}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{large100.length}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>100%</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{fmt$(large100.reduce((s,p)=>s+p.amount,0))}</td>
                </tr>
              )}
              {strata.map(st => (
                <tr key={st.id} style={trStyle}>
                  <td style={{...td,fontWeight:700,color:NAVY}}>{st.label}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{st.projects.length}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{st.n}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{fmtPct(st.n / st.projects.length)}</td>
                  <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{fmt$(st.projects.reduce((s,p)=>s+p.amount,0))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={sectionLabel}>Drawn Sample</div>
          <SampleTable projects={drawn} />

          <div style={{marginTop:16}}>
            <button style={secondaryBtn} onClick={() => exportDrawCSV(drawn, seed)}>
              ⬇ Export Sample as CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function SampleTable({ projects, storedIds, showMatch }) {
  return (
    <table style={{...tbl, marginBottom:8}}>
      <thead><tr>
        {['#','Project Name','Stratum','Reported Amount',showMatch&&'In Both'].filter(Boolean).map(h=><th key={h} style={th}>{h}</th>)}
      </tr></thead>
      <tbody>
        {projects.map((p, i) => {
          const inBoth = storedIds ? storedIds.has(String(p._id)) : null;
          return (
            <tr key={String(p._id)+i} style={{...trStyle, background: inBoth===false?'#fff0ee':undefined}}>
              <td style={{...td,color:'#8a96b0'}}>{i+1}</td>
              <td style={{...td,fontWeight:600,color:NAVY,maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</td>
              <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{p.isLarge ? '100% Certainty' : (p.stratum || '—')}</td>
              <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'right'}}>{fmt$(p.amount)}</td>
              {showMatch && <td style={{...td,fontWeight:700,color:inBoth?'#1a5c3a':'#c03a1a',fontFamily:"'Roboto Mono',monospace"}}>
                {inBoth == null ? '' : inBoth ? '✓' : '✗ Not in other'}
              </td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MetaCell({ label, value, mono }) {
  return (
    <div style={metaCell}>
      <div style={metaLabel}>{label}</div>
      <div style={{...metaValue, fontFamily: mono ? "'Roboto Mono',monospace" : undefined}}>{value}</div>
    </div>
  );
}

function exportDrawCSV(drawn, seed) {
  const rows = [
    ['External Verification Draw'],
    [`Seed: ${seed}`],
    [`Generated: ${new Date().toLocaleString()}`],
    [],
    ['#', 'Project Name', 'Stratum', 'Reported Amount'],
    ...drawn.map((p, i) => [i+1, p.name, p.isLarge ? '100% Certainty' : (p.stratum||''), p.amount]),
  ];
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `external_draw_seed${seed}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const modeRow      = { display:'flex', gap:12, marginBottom:24 };
const modeBtn      = { padding:'10px 20px', background:'white', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#5a6a8a', cursor:'pointer' };
const modeBtnActive= { background:NAVY, color:AMBER, border:`1.5px solid ${NAVY}`, fontWeight:700 };

const helpBox  = { background:'#e8ecf5', borderLeft:`3px solid ${NAVY}`, borderRadius:'0 6px 6px 0', padding:'14px 18px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, lineHeight:1.6, marginBottom:24 };
const stepCard = { background:'white', border:`1.5px solid ${BORDER}`, borderRadius:8, padding:28, marginBottom:20 };
const stepHeader={ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, color:NAVY, marginBottom:18, letterSpacing:'0.03em' };

const formSection = { marginBottom:24 };
const label       = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', color:'#5a6a8a', display:'block', marginBottom:6 };
const hint        = { fontFamily:"'Roboto Mono',monospace", fontSize:10, color:'#8a96b0', marginTop:4, lineHeight:1.5 };
const select      = { width:'100%', padding:'10px 12px', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, outline:'none', background:'white' };
const selectSm    = { width:'100%', padding:'8px 10px', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:11, color:NAVY, outline:'none', background:'white' };
const inputSm     = { width:'100%', padding:'8px 10px', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, outline:'none', background:'white', boxSizing:'border-box' };

const dropZone    = { border:`2px dashed ${BORDER}`, borderRadius:8, padding:'32px 24px', textAlign:'center', cursor:'pointer', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#8a96b0', marginBottom:16 };

const colMapGrid  = { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginTop:16 };
const colMapField = { display:'flex', flexDirection:'column' };

const settingsGrid = { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:16 };
const settingField = { display:'flex', flexDirection:'column' };

const primaryBtn  = { padding:'11px 22px', background:AMBER, color:NAVY, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, cursor:'pointer' };
const secondaryBtn= { padding:'9px 18px', background:'white', color:NAVY, border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, cursor:'pointer' };

const resultBanner= { borderRadius:8, padding:'20px 24px', marginBottom:0 };
const resultIcon  = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:18, color:'white', marginBottom:6 };
const resultMsg   = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'rgba(255,255,255,0.85)', lineHeight:1.6 };

const metaRow  = { display:'flex', gap:12, flexWrap:'wrap', margin:'16px 0' };
const metaCell = { background:'#f4f6fb', border:`1.5px solid ${BORDER}`, borderRadius:6, padding:'12px 16px', minWidth:100 };
const metaLabel= { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', color:'#8a96b0', marginBottom:4 };
const metaValue= { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:18, color:NAVY };

const errorBanner = { background:'#fff0ee', border:'1px solid #c03a1a', borderRadius:4, padding:'10px 14px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#c03a1a', marginTop:12 };

const sectionLabel = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'#8a96b0', margin:'24px 0 12px', paddingTop:20, borderTop:'1px solid #e8ecf5' };

const tbl    = { width:'100%', borderCollapse:'collapse', fontFamily:"'Roboto Mono',monospace", fontSize:12 };
const th     = { background:NAVY, color:AMBER, padding:'8px 12px', textAlign:'left', fontSize:10, letterSpacing:'0.06em', textTransform:'uppercase' };
const td     = { padding:'9px 12px', color:'#5a6a8a', verticalAlign:'middle' };
const trStyle= { borderBottom:'1px solid #f0f2f8' };
